<script>
  import Home from './routes/index.svelte';
  import Onboarding from './routes/onboarding.svelte';
  import Scan from './routes/scan.svelte';
  import Library from './routes/library.svelte';
  import Settings from './routes/settings.svelte';
  import OAuthCallback from './routes/OAuthCallback.svelte';
  import Auth from './routes/auth.svelte';
  import Admin from './routes/admin.svelte';
  import { appStatus, notifications } from './stores/ui.js';
  import { authState, initAuth, logout, getKnownUsers } from './stores/auth.js';
  import { fetchLicense } from './stores/license.js';
  import { onMount } from 'svelte';
  import { t, locale as localeStore } from 'svelte-i18n';
  import { loadLanguageSetting, saveLanguageSetting } from './i18n/languageStore.js';
  import { setAppLocale } from './i18n/index.js';
  import { applyTheme } from './lib/theme/applyTheme.js';
  import { loadThemeSettings } from './lib/theme/themeStore.js';
  import { toggleLightDark } from './lib/theme/toggleTheme.js';

  const navLinks = [
    { id: 'overview', labelKey: 'nav.overview' },
    { id: 'scan', labelKey: 'nav.scan' },
    { id: 'library', labelKey: 'nav.library' },
    { id: 'settings', labelKey: 'nav.settings' }
  ];

  let activeView = 'overview';
  let knownUsers = [];
  let lastSeenUserId = null;

  onMount(() => {
    initAuth();
    fetchLicense();
    knownUsers = getKnownUsers();
    const savedView = localStorage.getItem('ytcs:last-view');
    if (savedView) {
      activeView = savedView;
    }
    const params = new URLSearchParams(window.location.search);
    const isOAuthPath = window.location.pathname === '/oauth/callback';
    if (isOAuthPath || params.get('code') || params.get('error')) {
      activeView = 'oauth';
      // Keep the path, but drop query params after we captured them.
      // (OAuthCallback reads window.location.search on mount.)
      if (params.toString()) {
        history.replaceState({}, '', window.location.pathname);
      }
    }
  });

  $: localStorage.setItem('ytcs:last-view', activeView);

  async function syncLocaleWithActiveUser() {
    // Ensure we load the per-user language after auth switches the active userId
    const userId = $authState.userId || 'guest';
    if (userId === lastSeenUserId) return;
    lastSeenUserId = userId;

    try {
      const stored = await loadLanguageSetting();
      if (stored) {
        setAppLocale(stored);
      } else {
        // If this user has no saved language yet, carry over current locale.
        const current = $localeStore;
        await saveLanguageSetting(current);
      }
    } catch {
      // ignore
    }
  }

  async function handleToggleTheme() {
    await toggleLightDark();
    const theme = await loadThemeSettings();
    applyTheme(theme);
  }

  $: syncLocaleWithActiveUser();

  $: if (!$authState.isAuthenticated && knownUsers.length > 0 && activeView !== 'oauth') {
    activeView = 'auth';
  }
</script>

<div class="app-shell">
  <header class="topbar">
    <div class="brand">
      <div class="logo">YT</div>
      <div>
        <div class="title">{$t('app.name')}</div>
        <div class="subtitle">{$t('app.subtitle')}</div>
      </div>
    </div>
    <div class="header-actions">
      <button class="ghost" on:click={handleToggleTheme}>Theme</button>
      {#if $authState.isAuthenticated}
        <div class="pill">{$authState.email}</div>
        <button class="ghost" on:click={logout}>{$t('auth.logout')}</button>
        {#if $authState.role === 'admin'}
          <button class="ghost" on:click={() => (activeView = 'admin')}>{$t('nav.admin')}</button>
        {/if}
      {:else}
        <button class="ghost" on:click={() => (activeView = 'auth')}>{$t('auth.login')}</button>
      {/if}
    </div>
  </header>

  <nav class="tabs">
    {#each navLinks as link}
      <button
        class={`tab ${activeView === link.id ? 'active' : ''}`}
        on:click={() => (activeView = link.id)}
      >
        {$t(link.labelKey)}
      </button>
    {/each}
    <button class={`tab ${activeView === 'onboarding' ? 'active' : ''}`} on:click={() => (activeView = 'onboarding')}>
      {$t('nav.onboarding')}
    </button>
  </nav>

  <main class="content">
    {#if activeView === 'overview'}
      <Home />
    {:else if activeView === 'onboarding'}
      <Onboarding />
    {:else if activeView === 'scan'}
      <Scan />
    {:else if activeView === 'library'}
      <Library />
    {:else if activeView === 'settings'}
      <Settings />
    {:else if activeView === 'auth'}
      <Auth />
    {:else if activeView === 'admin'}
      <Admin />
    {:else if activeView === 'oauth'}
      <OAuthCallback />
    {:else}
      <Home />
    {/if}
  </main>

  {#if $notifications.length > 0}
    <div class="toasts">
      {#each $notifications.slice(0, 3) as notice}
        <div class={`toast ${notice.tone}`}>{notice.message}</div>
      {/each}
    </div>
  {/if}
</div>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');

  :global(:root) {
    color-scheme: dark;
    --bg: #1a1a1a;
    --bg-deep: #0f0f0f;
    --card: #111827;
    --ink: #ffffff;
    --muted: #9ca3af;
    --line: #374151;
    --accent: #dc2626;
    --accent-2: #ef4444;
    --shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
  }

  :global(*) {
    box-sizing: border-box;
  }

  :global(body) {
    margin: 0;
    font-family: "Space Grotesk", "IBM Plex Sans", system-ui, -apple-system, sans-serif;
    color: var(--ink);
    background: radial-gradient(circle at 15% 15%, var(--card) 0%, var(--bg) 40%, var(--bg-deep) 100%);
    min-height: 100vh;
  }

  :global(body::before) {
    content: "";
    position: fixed;
    inset: 0;
    background-image: radial-gradient(circle at 80% 10%, rgba(255, 138, 0, 0.15), transparent 40%),
      radial-gradient(circle at 20% 80%, rgba(42, 157, 143, 0.18), transparent 45%);
    pointer-events: none;
    z-index: 0;
  }

  .app-shell {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 2rem 2rem;
  }

  .topbar {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }

  .brand {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .logo {
    width: 48px;
    height: 48px;
    border-radius: 16px;
    background: conic-gradient(from 120deg, var(--accent), #fca311, #ffe3b3, var(--accent-2));
    display: grid;
    place-items: center;
    font-weight: 700;
  }

  .title {
    font-size: 1.2rem;
    font-weight: 700;
  }

  .subtitle {
    color: var(--muted);
    font-size: 0.9rem;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .pill {
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    font-size: 0.85rem;
  }

  .ghost {
    border: none;
    background: rgba(255, 255, 255, 0.08);
    color: var(--ink);
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 0.45rem 0.9rem;
    font-weight: 600;
    cursor: pointer;
  }


  .ghost.icon {
    width: 42px;
    height: 42px;
    padding: 0;
    display: inline-grid;
    place-items: center;
    font-size: 1.05rem;
    line-height: 1;
  }
  .tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .tab {
    border: none;
    background: rgba(255, 255, 255, 0.06);
    color: var(--ink);
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-weight: 600;
  }

  .tab.active {
    background: #192567;
    color: #03a12f;
    border: 1px solid rgba(3, 161, 47, 0.35);
  }

  .content {
    background: rgba(255, 255, 255, 0.04);
    color: var(--ink);
    border-radius: 24px;
    padding: 2rem;
    border: 1px solid var(--line);
    box-shadow: var(--shadow);
    min-height: 70vh;
  }

  .toasts {
    position: fixed;
    right: 1.5rem;
    bottom: 1.5rem;
    display: grid;
    gap: 0.5rem;
    z-index: 10;
  }

  .toast {
    background: rgba(17, 24, 39, 0.92);
    padding: 0.75rem 1rem;
    border-radius: 14px;
    border: 1px solid rgba(31, 41, 51, 0.12);
    box-shadow: var(--shadow);
    font-weight: 600;
  }

  .toast.success {
    border-color: rgba(42, 157, 143, 0.5);
  }

  .toast.error {
    border-color: rgba(220, 38, 38, 0.5);
  }

  .toast.warning {
    border-color: rgba(255, 138, 0, 0.6);
  }

  @media (max-width: 720px) {
    .app-shell {
      padding: 1.25rem;
    }

    .content {
      padding: 1.25rem;
    }
  }
</style>
